/**
 * Excel export of the Profit & Loss account cards report.
 *
 * Styling and assembly follow the trial balance export
 * (Components/LedgerReport/TrialBalanceReportHtml): an array-of-arrays is built while a
 * parallel `rowMeta` records the kind of each row, then styles, merges, row heights and
 * the RTL sheet view are applied per kind. The shared `applyWorkbookRtl` helper is reused
 * so the file opens right-to-left.
 *
 * The workbook has two sheets:
 *   1. the report as displayed — sections, palcodes, account cards and every subtotal;
 *   2. the detailed transactions, in the same Profit & Loss order, with context columns
 *      so they can be filtered and pivoted.
 * Nesting both in one sheet would give a single column two different meanings, which is
 * why the detail lives on its own sheet.
 */

import * as XLSX from 'xlsx-js-style';
import { applyWorkbookRtl, AMOUNT_NUMBER_FORMAT } from '../../../Utils/ExcelReport';
import { getCaseNumberLabel } from '../../../Utils/ReportUtils';

const PALETTE = {
    primary: 'FF2F4BFF',
    primaryDark: 'FF1E308F',
    headerFont: 'FFFFFFFF',
    opening: 'FFEFF2FF',
    subFill: 'FFE3E9FF',
    totalFill: 'FFD7DFFF',
    groupFill: 'FFBECBFF',
    grandFill: 'FFA9BBFF',
    stripe: 'FFF6F8FF',
    detail: 'FFF0F1F6',
    text: 'FF1F2937',
    muted: 'FF55596B',
    border: 'FFC3CBDB',
};

const thinBorder = { style: 'thin', color: { rgb: PALETTE.border } };
const allThin = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };

const STYLES = {
    clientLine: { font: { bold: true, sz: 14, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'right' } },
    reportTitle: { font: { bold: true, sz: 20, color: { rgb: PALETTE.primary } }, alignment: { horizontal: 'center' } },
    dateLine: { font: { bold: true, sz: 12, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'center' } },
    metaLine: { font: { italic: true, sz: 11, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'center' } },
    sectionHeader: { font: { bold: true, sz: 15, color: { rgb: PALETTE.primaryDark } }, alignment: { horizontal: 'right', vertical: 'center' } },
    sectionTitle: { font: { bold: true, sz: 13, color: { rgb: PALETTE.primary }, underline: true }, alignment: { horizontal: 'right', vertical: 'center' } },
    palCodeLabel: { font: { bold: true, sz: 12, color: { rgb: PALETTE.text } }, fill: { fgColor: { rgb: PALETTE.opening } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    header: { font: { bold: true, sz: 12, color: { rgb: PALETTE.headerFont } }, fill: { fgColor: { rgb: PALETTE.primary } }, alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border: allThin },
    dataText: { font: { sz: 11, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    dataNum: { font: { sz: 11, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    stripeText: { font: { sz: 11, color: { rgb: PALETTE.text } }, fill: { fgColor: { rgb: PALETTE.stripe } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    stripeNum: { font: { sz: 11, color: { rgb: PALETTE.text } }, fill: { fgColor: { rgb: PALETTE.stripe } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    detailText: { font: { sz: 10, color: { rgb: PALETTE.muted } }, fill: { fgColor: { rgb: PALETTE.detail } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    detailNum: { font: { sz: 10, color: { rgb: PALETTE.muted } }, fill: { fgColor: { rgb: PALETTE.detail } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    subtotalText: { font: { bold: true, sz: 12, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.totalFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    subtotalNum: { font: { bold: true, sz: 12, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.totalFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    categoryText: { font: { bold: true, sz: 12, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.subFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    categoryNum: { font: { bold: true, sz: 12, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.subFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    groupText: { font: { bold: true, sz: 13, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.groupFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    groupNum: { font: { bold: true, sz: 13, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.groupFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    grandText: { font: { bold: true, sz: 14, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.grandFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    grandNum: { font: { bold: true, sz: 14, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.grandFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
};

const ROW_HEIGHT = {
    clientLine: 24, reportTitle: 32, dateLine: 20, metaLine: 18,
    sectionHeader: 24, sectionTitle: 22, palCodeLabel: 20, header: 26,
    data: 18, stripe: 18, detail: 16, subtotal: 22, category: 22,
    group: 24, grand: 26, blank: 8,
};

const MERGED_KINDS = ['clientLine', 'reportTitle', 'dateLine', 'metaLine', 'sectionHeader', 'sectionTitle', 'palCodeLabel'];

const KIND_STYLES = {
    data: ['dataText', 'dataNum'],
    stripe: ['stripeText', 'stripeNum'],
    detail: ['detailText', 'detailNum'],
    subtotal: ['subtotalText', 'subtotalNum'],
    category: ['categoryText', 'categoryNum'],
    group: ['groupText', 'groupNum'],
    grand: ['grandText', 'grandNum'],
};

/** Shared sheet writer: applies merges, widths, heights, RTL and per-kind cell styles. */
const createStyledSheet = ({ aoa, rowMeta, merges, colCount, colWidths, numericColIndexes }) => {
    const numericSet = new Set(numericColIndexes);
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    ws['!merges'] = merges.filter(Boolean);
    ws['!cols'] = Array.from({ length: colCount }, (_, c) => ({ wch: colWidths[c] ?? 18 }));
    ws['!rows'] = rowMeta.map((meta) => ({ hpt: ROW_HEIGHT[meta.kind] || 18 }));
    ws['!views'] = [{ RTL: true }];

    aoa.forEach((row, r) => {
        const meta = rowMeta[r];
        if (!meta || meta.kind === 'blank') return;

        if (MERGED_KINDS.includes(meta.kind)) {
            const addr = XLSX.utils.encode_cell({ r, c: 0 });
            if (ws[addr]) ws[addr].s = STYLES[meta.kind];
            return;
        }

        for (let c = 0; c < colCount; c += 1) {
            const addr = XLSX.utils.encode_cell({ r, c });
            let cell = ws[addr];
            if (!cell) { cell = { t: 's', v: '' }; ws[addr] = cell; }

            const isNum = numericSet.has(c);
            if (meta.kind === 'header') {
                cell.s = STYLES.header;
            } else {
                const [textStyle, numStyle] = KIND_STYLES[meta.kind] || KIND_STYLES.data;
                cell.s = isNum ? STYLES[numStyle] : STYLES[textStyle];
            }
            if (isNum && typeof cell.v === 'number') cell.z = AMOUNT_NUMBER_FORMAT;
        }
    });

    return ws;
};

/** Sheet 1 — the report as displayed, in Profit & Loss order. */
const buildSummarySheet = ({ model, titleLines, showPercentage, cardLabel, t }) => {
    const header = showPercentage
        ? [t('AccountCard'), t('Balance'), t('PercentOfSales')]
        : [t('AccountCard'), t('Balance')];
    const colCount = header.length;

    const aoa = [];
    const rowMeta = [];
    const merges = [];
    const blankRow = () => new Array(colCount).fill('');

    const pushMerged = (text, kind) => {
        const r = aoa.length;
        const row = blankRow();
        row[0] = text;
        aoa.push(row);
        rowMeta.push({ kind });
        merges.push({ s: { r, c: 0 }, e: { r, c: Math.max(colCount - 1, 0) } });
    };
    const pushRow = (label, balance, percentage, kind) => {
        const row = showPercentage ? [label, balance, percentage] : [label, balance];
        aoa.push(row);
        rowMeta.push({ kind });
    };
    const pushBlank = () => { aoa.push(blankRow()); rowMeta.push({ kind: 'blank' }); };

    titleLines.forEach((line) => pushMerged(line.text, line.kind));
    pushBlank();

    let cardIndex = 0;
    model.sections.forEach((section) => {
        if (section.showAgLabel) pushMerged(section.agLabel, 'sectionHeader');
        pushMerged(section.detailLabel, 'sectionTitle');
        aoa.push(header.slice());
        rowMeta.push({ kind: 'header' });

        section.palCodes.forEach((palCode) => {
            pushMerged(palCode.palCodeLabel, 'palCodeLabel');
            palCode.cards.forEach((card) => {
                pushRow(cardLabel(card), card.balance, card.percentage, cardIndex % 2 === 1 ? 'stripe' : 'data');
                cardIndex += 1;
            });
            pushRow(`${t('Total')}: ${palCode.palCodeLabel}`, palCode.total, palCode.percentage, 'subtotal');
        });

        pushRow(`${t('Total')}: ${section.categoryLabel}`, section.categoryTotal, section.categoryPercentage, 'category');

        if (section.isLastCategoryInGroup) {
            pushRow(`${t('Total')} ${section.accountingGroupName}:`, section.groupTotal, section.groupPercentage, 'group');
        }
        if (section.showRunningTotal) {
            pushRow(
                t('ProfitAndLossAfterGroup', { group: section.accountingGroupName }),
                section.runningTotal,
                section.runningPercentage,
                'category'
            );
        }
        pushBlank();
    });

    pushRow(t('TotalProfitAndLossInReport'), model.grandTotal, model.grandTotalPercentage, 'grand');

    return createStyledSheet({
        aoa,
        rowMeta,
        merges,
        colCount,
        colWidths: [46, 20, 16],
        numericColIndexes: showPercentage ? [1, 2] : [1],
    });
};

/** Sheet 2 — every detailed transaction, in the same order, with context columns. */
const buildTransactionsSheet = ({ model, titleLines, cardLabel, formatDate, t }) => {
    const header = [
        t('AccountingGroup'),
        t('BalanceClassificationCategory'),
        t('BalanceClassification'),
        t('AccountCard'),
        t('Credit Account'),
        t('Debit Account'),
        t('Debit'),
        t('Credit'),
        t('Balance'),
        t('ValueDate'),
        t('ReferenceDate'),
        t('Reference1'),
        t('Reference2'),
        t('Details'),
    ];
    const colCount = header.length;
    const numericColIndexes = [6, 7, 8];

    const aoa = [];
    const rowMeta = [];
    const merges = [];
    const blankRow = () => new Array(colCount).fill('');

    const pushMerged = (text, kind) => {
        const r = aoa.length;
        const row = blankRow();
        row[0] = text;
        aoa.push(row);
        rowMeta.push({ kind });
        merges.push({ s: { r, c: 0 }, e: { r, c: Math.max(colCount - 1, 0) } });
    };

    titleLines.forEach((line) => pushMerged(line.text, line.kind));
    aoa.push(blankRow());
    rowMeta.push({ kind: 'blank' });

    aoa.push(header.slice());
    rowMeta.push({ kind: 'header' });

    let rowIndex = 0;
    model.sections.forEach((section) => {
        section.palCodes.forEach((palCode) => {
            palCode.cards.forEach((card) => {
                if (!card.transactions.length) return;

                card.transactions.forEach((transaction) => {
                    aoa.push([
                        section.agLabel,
                        section.categoryLabel,
                        palCode.palCodeLabel,
                        cardLabel(card),
                        transaction.creditAccountName || '',
                        transaction.debitAccountName || '',
                        Number(transaction.debitAccount) || 0,
                        Number(transaction.creditAccount) || 0,
                        Number(transaction.balance) || 0,
                        transaction.uploadDate ? formatDate(transaction.uploadDate) : '',
                        transaction.documentDate ? formatDate(transaction.documentDate) : '',
                        transaction.reference1 || '',
                        transaction.reference2 || '',
                        transaction.description ? t(transaction.description, { defaultValue: transaction.description }) : '',
                    ]);
                    rowMeta.push({ kind: rowIndex % 2 === 1 ? 'stripe' : 'data' });
                    rowIndex += 1;
                });

                const row = blankRow();
                row[3] = `${t('Total')}: ${cardLabel(card)}`;
                row[6] = card.transactions.reduce((sum, tr) => sum + (Number(tr.debitAccount) || 0), 0);
                row[7] = card.transactions.reduce((sum, tr) => sum + (Number(tr.creditAccount) || 0), 0);
                row[8] = card.transactionsTotal;
                aoa.push(row);
                rowMeta.push({ kind: 'subtotal' });
            });
        });
    });

    return createStyledSheet({
        aoa,
        rowMeta,
        merges,
        colCount,
        colWidths: [22, 26, 20, 30, 26, 26, 16, 16, 16, 14, 14, 14, 14, 30],
        numericColIndexes,
    });
};

export const downloadProfitAndLossCardsReportExcel = ({
    model,
    client,
    trialBalanceData,
    title,
    period,
    showPercentage,
    showAccountCodeId,
    formatDate,
    t,
}) => {
    if (!model?.sections?.length) return false;

    const cardLabel = (card) => {
        const name = card.accountCardName || '';
        return showAccountCodeId && card.accountCardId ? `${card.accountCardId} ${name}`.trim() : name;
    };

    const clientName = trialBalanceData?.clientName || client?.name || '';
    const clientCase = trialBalanceData?.clientCase || client?.caseId || '';
    const caseLabel = getCaseNumberLabel(client?.caseType);

    const titleLines = [
        { text: `${caseLabel}: ${clientCase}    ${clientName}`, kind: 'clientLine' },
        { text: title, kind: 'reportTitle' },
        { text: period, kind: 'dateLine' },
        { text: `${t('TotalIncome')}: ${model.totalIncome.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, kind: 'metaLine' },
    ].filter((line) => line.text);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
        wb,
        buildSummarySheet({ model, titleLines, showPercentage, cardLabel, t }),
        'ProfitAndLoss'
    );

    if (model.transactionsCount > 0) {
        XLSX.utils.book_append_sheet(
            wb,
            buildTransactionsSheet({ model, titleLines, cardLabel, formatDate, t }),
            'Transactions'
        );
    }

    applyWorkbookRtl(wb);
    XLSX.writeFile(wb, `${clientName || 'Report'}_ProfitAndLossCards.xlsx`);
    return true;
};

export default downloadProfitAndLossCardsReportExcel;
